/**
 * Selectors for UI-Model-1 (API-Direct Approach)
 *
 * Traversal helpers for the nested API structure.
 * Since API format is nested (containers → content.items), these selectors
 * traverse the structure to find elements.
 *
 * Performance note: These are O(n) traversals. Use memoization in components
 * with useMemo for frequently accessed data.
 */

import {
  UIModelState,
  UIModelAPI,
  ContainerAPI,
  CanvasAPI,
  GroupAPI,
  PageAPI,
  ItemAPI,
  FieldAPI,
  WidgetAPI,
  isFieldAPI,
  isWidgetAPI,
  isItemAPI,
  ElementPath,
  LookupResult,
} from '../types/state.types';

// ============================================================================
// Model Selectors
// ============================================================================

/**
 * True when the model is in Free style composition mode — submodel locking,
 * global uniqueness and page-locking are lifted. Absent flag = restricted,
 * so every pre-existing model keeps today's behavior.
 */
export const isFreestyle = (
  model: UIModelAPI | null | undefined
): boolean => model?.composition_mode === 'freestyle';

/** isFreestyle read straight off editor state. */
export const selectIsFreestyle = (state: UIModelState): boolean =>
  isFreestyle(state.model);

// ============================================================================
// Canvas Selectors
// ============================================================================

/** Get canvas by ID */
export const selectCanvas = (
  state: UIModelState,
  canvasId: string
): CanvasAPI | undefined =>
  state.model?.layout.canvas[canvasId];

/** Get all canvas IDs */
export const selectCanvasIds = (state: UIModelState): string[] =>
  state.model ? Object.keys(state.model.layout.canvas) : [];

/** Get the active canvas ID */
export const selectActiveCanvasId = (state: UIModelState): string | null => {
  const { activeGroupId, activePageId } = state.editorState;
  if (!activeGroupId || !activePageId || !state.model) return null;

  // Find the active page to get its canvasId
  const group = state.model.layout.navigation.groups.find(g => g.id === activeGroupId);
  const page = group?.pages.find(p => p.id === activePageId);

  // Return the page's canvasId (UUID format from API)
  return page?.canvasId ?? null;
};

/** Get the canvas for the currently active page */
export const selectActiveCanvas = (state: UIModelState): CanvasAPI | undefined => {
  const canvasId = selectActiveCanvasId(state);
  if (!canvasId || !state.model) return undefined;

  return state.model.layout.canvas[canvasId];
};

// ============================================================================
// Container Selectors (Traverse nested structure)
// ============================================================================

/** Get top-level containers for a canvas */
export const selectContainersForCanvas = (
  state: UIModelState,
  canvasId: string
): ContainerAPI[] => {
  const canvas = selectCanvas(state, canvasId);
  return canvas?.containers || [];
};

/** Get top-level containers for a canvas (alias for selectContainersForCanvas) */
export const selectTopLevelContainersForCanvas = selectContainersForCanvas;

/** Get top-level containers for the current page */
export const selectCurrentPageContainers = (state: UIModelState): ContainerAPI[] => {
  const canvasId = selectActiveCanvasId(state);
  if (!canvasId) return [];
  return selectContainersForCanvas(state, canvasId);
};

/**
 * Find container by ID (traverses all canvases and nested containers)
 * Returns the container and its path
 */
export const selectContainerById = (
  state: UIModelState,
  containerId: string
): LookupResult<ContainerAPI> => {
  if (!state.model) {
    return { element: undefined, path: null, parent: null };
  }

  for (const [canvasId, canvas] of Object.entries(state.model.layout.canvas)) {
    // Handle undefined containers array
    const containers = canvas?.containers;
    if (!containers || !Array.isArray(containers)) continue;

    const result = findContainerInList(containers, containerId, canvasId, []);
    if (result.element) {
      return result;
    }
  }

  return { element: undefined, path: null, parent: null };
};

/** Helper: recursively find container in nested structure */
const findContainerInList = (
  containers: ContainerAPI[],
  targetId: string,
  canvasId: string,
  pathSoFar: string[],
  parent: ContainerAPI | null = null
): LookupResult<ContainerAPI> => {
  // Safety check for non-iterable containers
  if (!containers || !Array.isArray(containers)) {
    return { element: undefined, path: null, parent: null };
  }

  for (const container of containers) {
    if (container.id === targetId) {
      return {
        element: container,
        path: { canvasId, containerPath: [...pathSoFar, container.id] },
        parent,
      };
    }

    // Check nested containers (with optional chaining)
    const nestedContainers = container.content?.nestedContainers;
    if (nestedContainers && Array.isArray(nestedContainers)) {
      const result = findContainerInList(
        nestedContainers,
        targetId,
        canvasId,
        [...pathSoFar, container.id],
        container
      );
      if (result.element) {
        return result;
      }
    }
  }

  return { element: undefined, path: null, parent: null };
};

/** Get all containers in a canvas (flattened, including nested) */
export const selectAllContainersInCanvas = (
  state: UIModelState,
  canvasId: string
): ContainerAPI[] => {
  const canvas = selectCanvas(state, canvasId);
  if (!canvas) return [];

  const result: ContainerAPI[] = [];
  const collectContainers = (containers: ContainerAPI[]) => {
    if (!containers || !Array.isArray(containers)) return;
    for (const container of containers) {
      result.push(container);
      const nestedContainers = container.content?.nestedContainers;
      if (nestedContainers && Array.isArray(nestedContainers)) {
        collectContainers(nestedContainers);
      }
    }
  };
  collectContainers(canvas.containers || []);
  return result;
};

/** Get nested (child) containers of a container */
export const selectChildContainers = (
  state: UIModelState,
  containerId: string
): ContainerAPI[] => {
  const { element: container } = selectContainerById(state, containerId);
  return container?.content?.nestedContainers || [];
};

/** Get the selected container */
export const selectSelectedContainer = (
  state: UIModelState
): ContainerAPI | undefined => {
  const { selectedContainerId } = state.editorState;
  if (!selectedContainerId) return undefined;
  return selectContainerById(state, selectedContainerId).element;
};

/** Get container ancestry path (from root to container) */
export const selectContainerPath = (
  state: UIModelState,
  containerId: string
): ContainerAPI[] => {
  const path: ContainerAPI[] = [];
  const { path: elementPath } = selectContainerById(state, containerId);

  if (!elementPath) return path;

  // Build path by looking up each container in the path
  for (const pathContainerId of elementPath.containerPath) {
    const { element } = selectContainerById(state, pathContainerId);
    if (element) {
      path.push(element);
    }
  }

  return path;
};

/** Get the root container for a given container */
export const selectRootContainer = (
  state: UIModelState,
  containerId: string
): ContainerAPI | undefined => {
  const path = selectContainerPath(state, containerId);
  return path[0];
};

/** Get all container IDs in the same root hierarchy as the given container */
export const selectContainerIdsInRootHierarchy = (
  state: UIModelState,
  containerId: string
): Set<string> => {
  const root = selectRootContainer(state, containerId);
  if (!root) return new Set();
  const ids = new Set<string>();
  const collect = (container: ContainerAPI) => {
    ids.add(container.id);
    (container.content?.nestedContainers || []).forEach(nested => collect(nested));
  };
  collect(root);
  return ids;
};

// ============================================================================
// Item Selectors (Traverse container.content.items[])
// ============================================================================

/** Type guard to check if item has type property (filters out NestedContainerAPI) */
const hasTypeProperty = (item: unknown): item is { type: string } =>
  typeof item === 'object' && item !== null && 'type' in item;

/** Get items for a container */
export const selectItemsForContainer = (
  state: UIModelState,
  containerId: string
): ItemAPI[] => {
  const { element: container } = selectContainerById(state, containerId);
  if (!container) return [];

  // Handle undefined content or items (e.g., newly created/duplicated containers)
  const items = container.content?.items;
  if (!items) return [];

  // Filter to item types (field, category, connector, widget) — excludes NestedContainerAPI which has no type
  return items.filter(
    (item): item is ItemAPI => isItemAPI(item)
  );
};

/**
 * Find item by ID (traverses all containers)
 * Returns the item and its location
 */
export const selectItemById = (
  state: UIModelState,
  itemId: string
): LookupResult<ItemAPI> => {
  if (!state.model) {
    return { element: undefined, path: null, parent: null };
  }

  for (const [canvasId, canvas] of Object.entries(state.model.layout.canvas)) {
    const result = findItemInContainers(canvas.containers, itemId, canvasId, []);
    if (result.element) {
      return result;
    }
  }

  return { element: undefined, path: null, parent: null };
};

/** Helper: Type guard for item with id property */
const hasIdProperty = (item: unknown): item is { id: string } =>
  typeof item === 'object' && item !== null && 'id' in item;

/** Helper: recursively find item in containers */
const findItemInContainers = (
  containers: ContainerAPI[],
  itemId: string,
  canvasId: string,
  pathSoFar: string[]
): LookupResult<ItemAPI> => {
  for (const container of containers) {
    // Check items in this container (handle undefined content/items)
    const items = container.content?.items;
    if (items) {
      const item = items.find(
        (i): i is ItemAPI =>
          isItemAPI(i) &&
          i.id === itemId
      );

      if (item) {
        return {
          element: item,
          path: { canvasId, containerPath: [...pathSoFar, container.id], itemId },
          parent: container,
        };
      }
    }

    // Check nested containers
    if (container.content?.nestedContainers) {
      const result = findItemInContainers(
        container.content.nestedContainers,
        itemId,
        canvasId,
        [...pathSoFar, container.id]
      );
      if (result.element) {
        return result;
      }
    }
  }

  return { element: undefined, path: null, parent: null };
};

/** Get the selected item */
export const selectSelectedItem = (state: UIModelState): ItemAPI | undefined => {
  const { selectedItemId } = state.editorState;
  if (!selectedItemId) return undefined;
  return selectItemById(state, selectedItemId).element;
};

/** Get all fields in a container */
export const selectFieldsForContainer = (
  state: UIModelState,
  containerId: string
): FieldAPI[] => {
  return selectItemsForContainer(state, containerId).filter(isFieldAPI);
};

/** Get all widgets in a container */
export const selectWidgetsForContainer = (
  state: UIModelState,
  containerId: string
): WidgetAPI[] => {
  return selectItemsForContainer(state, containerId).filter(isWidgetAPI);
};

/** Get categories for a container */
export const selectCategoriesForContainer = (
  state: UIModelState,
  containerId: string
): ItemAPI[] => {
  return selectItemsForContainer(state, containerId).filter(
    (item) => isFieldAPI(item) && item.fieldType === 'category'
  );
};

// ============================================================================
// Navigation Selectors
// ============================================================================

/** Get all groups (sorted by order) */
export const selectGroups = (state: UIModelState): GroupAPI[] =>
  state.model?.layout.navigation.groups
    .slice()
    .sort((a, b) => a.order - b.order) || [];

/** Get group by ID */
export const selectGroupById = (
  state: UIModelState,
  groupId: string
): GroupAPI | undefined =>
  state.model?.layout.navigation.groups.find(g => g.id === groupId);

/** Get pages for a group (sorted by order) */
export const selectPagesForGroup = (
  state: UIModelState,
  groupId: string
): PageAPI[] => {
  const group = selectGroupById(state, groupId);
  return group?.pages.slice().sort((a, b) => a.order - b.order) || [];
};

/** Get page by ID */
export const selectPageById = (
  state: UIModelState,
  pageId: string
): PageAPI | undefined => {
  if (!state.model) return undefined;

  for (const group of state.model.layout.navigation.groups) {
    const page = group.pages.find(p => p.id === pageId);
    if (page) return page;
  }
  return undefined;
};

/** Get the active group */
export const selectActiveGroup = (state: UIModelState): GroupAPI | undefined => {
  const { activeGroupId } = state.editorState;
  if (!activeGroupId) return undefined;
  return selectGroupById(state, activeGroupId);
};

/** Get the active page */
export const selectActivePage = (state: UIModelState): PageAPI | undefined => {
  const { activePageId } = state.editorState;
  if (!activePageId) return undefined;
  return selectPageById(state, activePageId);
};

// ============================================================================
// Submodel Selectors
// ============================================================================

/** Get the submodel for a container */
export const selectSubmodelForContainer = (
  state: UIModelState,
  containerId: string
) => {
  const { element: container } = selectContainerById(state, containerId);
  return container?.content.submodel || null;
};

/** Get effective submodel ID (inherits from parent if not set) */
export const selectEffectiveSubmodelId = (
  state: UIModelState,
  containerId: string
): string | null => {
  const path = selectContainerPath(state, containerId);

  // Walk up the path from current to root
  for (let i = path.length - 1; i >= 0; i--) {
    const container = path[i];
    if (container.content.submodel?.id) {
      return container.content.submodel.id;
    }
  }

  return null;
};

/** Get all containers with a specific submodel */
export const selectContainersWithSubmodel = (
  state: UIModelState,
  submodelId: string
): ContainerAPI[] => {
  if (!state.model) return [];

  const result: ContainerAPI[] = [];

  // Iterate over canvas entries using their actual UUID keys
  for (const [canvasId, canvas] of Object.entries(state.model.layout.canvas)) {
    const containers = selectAllContainersInCanvas(state, canvasId);
    result.push(...containers.filter(c => c.content.submodel?.id === submodelId));
  }

  return result;
};

// ============================================================================
// Header/Footer/Theme Selectors
// ============================================================================

/** Get header configuration */
export const selectHeader = (state: UIModelState) =>
  state.model?.layout.header || null;

/** Get footer configuration */
export const selectFooter = (state: UIModelState) =>
  state.model?.layout.footer || null;

/** Get theme configuration */
export const selectTheme = (state: UIModelState) =>
  state.model?.theme || null;

/** Get custom CSS */
export const selectCustomCss = (state: UIModelState) =>
  state.model?.customCss || null;

/** Get navigation configuration */
export const selectNavigationConfig = (state: UIModelState) =>
  state.model?.layout.navigation.configuration || null;

/** Get shared widgets configuration */
export const selectSharedWidgets = (state: UIModelState) =>
  state.model?.layout.sharedWidgets || null;

// ============================================================================
// Dirty State Selectors
// ============================================================================

/** Check if a specific section is dirty */
export const selectIsSectionDirty = (
  state: UIModelState,
  section: string
): boolean => state.dirtyTracker.sections.has(section as any);

/** Get all dirty sections */
export const selectDirtySections = (state: UIModelState): string[] =>
  Array.from(state.dirtyTracker.sections);

/** Check if any section is dirty */
export const selectHasUnsavedChanges = (state: UIModelState): boolean =>
  state.dirtyTracker.sections.size > 0 || state.dirtyTracker.canvasPages.size > 0;

/** Get dirty canvas IDs */
export const selectDirtyCanvasIds = (state: UIModelState): string[] =>
  Array.from(state.dirtyTracker.canvasPages);

// ============================================================================
// History Selectors
// ============================================================================

/** Check if undo is available */
export const selectCanUndo = (state: UIModelState): boolean =>
  state.history.past.length > 0;

/** Check if redo is available */
export const selectCanRedo = (state: UIModelState): boolean =>
  state.history.future.length > 0;

/** Get the description of the next undo operation */
export const selectUndoDescription = (state: UIModelState): string | null => {
  const lastEntry = state.history.past[state.history.past.length - 1];
  return lastEntry?.description || null;
};

/** Get the description of the next redo operation */
export const selectRedoDescription = (state: UIModelState): string | null => {
  const nextEntry = state.history.future[0];
  return nextEntry?.description || null;
};

/** Get the history length */
export const selectHistoryLength = (state: UIModelState): number =>
  state.history.past.length;

// ============================================================================
// Model Metadata Selectors
// ============================================================================

/** Get model ID */
export const selectModelId = (state: UIModelState): string | null =>
  state.model?.id || null;

/** Get model name */
export const selectModelName = (state: UIModelState): string | null =>
  state.model?.name || null;

/** Check if model is loaded */
export const selectIsModelLoaded = (state: UIModelState): boolean =>
  state.model !== null;

/** Check if model is loading */
export const selectIsLoading = (state: UIModelState): boolean =>
  state.isLoading;

/** Check if model is saving */
export const selectIsSaving = (state: UIModelState): boolean =>
  state.isSaving;

/** Get last error */
export const selectError = (state: UIModelState): string | null =>
  state.error;

// ============================================================================
// Factory Selectors (for use with useMemo)
// ============================================================================

/**
 * Creates a memoized selector for containers on a page
 * Usage: const selector = useMemo(() => makeSelectContainersForCanvas(canvasId), [canvasId])
 */
export const makeSelectContainersForCanvas = (canvasId: string) =>
  (state: UIModelState) => selectContainersForCanvas(state, canvasId);

/**
 * Creates a memoized selector for items in a container
 */
export const makeSelectItemsForContainer = (containerId: string) =>
  (state: UIModelState) => selectItemsForContainer(state, containerId);

/**
 * Creates a memoized selector for child containers
 */
export const makeSelectChildContainers = (containerId: string) =>
  (state: UIModelState) => selectChildContainers(state, containerId);

/**
 * Creates a memoized selector for container path
 */
export const makeSelectContainerPath = (containerId: string) =>
  (state: UIModelState) => selectContainerPath(state, containerId);

/**
 * Creates a memoized selector for pages in a group
 */
export const makeSelectPagesForGroup = (groupId: string) =>
  (state: UIModelState) => selectPagesForGroup(state, groupId);
